import * as React from 'react'
import { IconUserCircle } from '@tabler/icons-react'
import { cn } from '@/lib/utils'

export interface ModuleCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  imageUrl?: string
  imageAlt?: string
  author: string
  authorAvatarUrl?: string
  students?: number
  tests?: number
  active?: boolean
  as?: 'div' | 'button' | 'a'
  href?: string
  onSelect?: () => void
}

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join('')

export const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  imageUrl,
  imageAlt = title,
  author,
  authorAvatarUrl,
  students,
  tests,
  active,
  as = 'div',
  className,
  href,
  onSelect,
  ...rest
}) => {
  const Component: any = as === 'a' ? 'a' : as === 'button' ? 'button' : 'div'

  return (
    <Component
      className={cn(
        'group relative flex w-[295px] flex-col rounded-lg border bg-white shadow-sm transition-colors',
        'border-[#EAECF0] hover:border-[#0057B8]/60 focus-visible:ring-2 focus-visible:ring-[#0057B8]/30 focus-visible:outline-none',
        active && 'border-[#0057B8]',
        as === 'button' && 'text-left',
        className
      )}
      href={Component === 'a' ? href : undefined}
      onClick={(e: React.MouseEvent) => {
        onSelect?.()
        rest.onClick?.(e as any)
      }}
      {...rest}
    >
      <div className='relative aspect-[295/180] w-full overflow-hidden rounded-t-lg bg-neutral-100'>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={imageAlt}
            className='size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]'
            loading='lazy'
          />
        ) : (
          <IconUserCircle />
        )}
      </div>
      <div className='flex flex-1 flex-col gap-3 p-4'>
        <div className='flex items-center gap-3'>
          {authorAvatarUrl ? (
            <img
              src={authorAvatarUrl}
              alt={author}
              className='h-8 w-8 rounded-full object-cover'
              loading='lazy'
            />
          ) : (
            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#E0E7EF] text-xs font-semibold text-[#002652]'>
              {getInitials(author)}
            </div>
          )}
          <span className='text-sm font-medium text-[#002652]'>{author}</span>
        </div>
        <h3 className='line-clamp-2 min-h-[56px] text-lg leading-snug font-semibold text-[#002652]'>
          {title}
        </h3>
        <div className='mt-auto flex gap-6 text-sm font-medium text-[#667085]'>
          {typeof students === 'number' && (
            <span className='inline-flex items-center gap-1.5'>
              <img
                src={new URL(
                  '@/assets/svg/student-module.svg',
                  import.meta.url
                ).toString()}
                alt='Students'
                className='h-4 w-4'
              />
              <span>
                {students} {students === 1 ? 'student' : 'students'}
              </span>
            </span>
          )}
          {typeof tests === 'number' && (
            <span className='inline-flex items-center gap-1.5'>
              <img
                src={new URL(
                  '@/assets/svg/test-module.svg',
                  import.meta.url
                ).toString()}
                alt='Students'
                className='h-4 w-4'
              />
              <span>
                {tests} {tests === 1 ? 'test' : 'tests'}
              </span>
            </span>
          )}
        </div>
      </div>
    </Component>
  )
}

export default ModuleCard
